import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { NetworkModel, PriceCheckItem, RangeDays, SalesOverview } from '../lib/analytics';

/**
 * Merchant analytics: the store's own sales, the network's hot models and where this store
 * stands on each.
 *
 * Three independent loads, so the chart is not held up by the slower ones:
 *   get_merchant_sales_overview   (SQL, membership-checked)   the store's own numbers
 *   get_merchant_network_insights (SQL, membership-checked)   ranked models + status
 *   merchant-analytics            (edge function)             live shelf price vs the network
 * The price check needs the store's current Shopify price, hence the function; it fails soft —
 * the page is complete without it, the "priced above" group is just empty.
 */
export const useMerchantAnalytics = (shopId: string | null, days: RangeDays) => {
  const [overview, setOverview] = useState<SalesOverview | null>(null);
  const [models, setModels] = useState<NetworkModel[] | null>(null);
  const [prices, setPrices] = useState<PriceCheckItem[]>([]);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const seq = useRef(0);

  const loadInsights = useCallback(async () => {
    if (!shopId) return;
    const { data, error: err } = await supabase.rpc('get_merchant_network_insights', {
      p_merchant_id: shopId,
      p_days: days,
    });
    if (err) throw err;
    return ((data as any)?.models ?? []) as NetworkModel[];
  }, [shopId, days]);

  useEffect(() => {
    const run = ++seq.current;
    setOverview(null);
    setModels(null);
    setPrices([]);
    setError(null);
    if (!shopId) return;

    const current = () => run === seq.current;
    const fail = (e: any) => current() && setError(e?.message || 'Failed to load analytics');

    let tz = 'UTC';
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {}

    supabase
      .rpc('get_merchant_sales_overview', { p_merchant_id: shopId, p_days: days, p_tz: tz })
      .then(({ data, error: err }) => {
        if (err) return fail(err);
        if (current()) setOverview(data as SalesOverview);
      });

    loadInsights()
      .then((list) => current() && setModels(list ?? []))
      .catch(fail);

    setPricesLoading(true);
    supabase.functions
      .invoke('merchant-analytics', { body: { shop_id: shopId, days } })
      .then(({ data, error: err }) => {
        if (!current()) return;
        // Soft failure by design: see the note above.
        if (err) console.warn('Price check unavailable:', err.message);
        setPrices(((data as any)?.items ?? []) as PriceCheckItem[]);
      })
      .finally(() => current() && setPricesLoading(false));
  }, [shopId, days, loadInsights]);

  const withPending = useCallback(async (sku: string, work: () => Promise<void>) => {
    setPending((p) => new Set(p).add(sku));
    try {
      await work();
    } finally {
      setPending((p) => {
        const next = new Set(p);
        next.delete(sku);
        return next;
      });
    }
  }, []);

  /** Select a model from the connected suppliers that carry it. Returns their names. */
  const addModel = useCallback(
    async (sku: string): Promise<string[]> => {
      if (!shopId) throw new Error('No shop selected');
      let added: string[] = [];
      await withPending(sku, async () => {
        const { data, error: err } = await supabase.rpc('analytics_add_model_to_store', {
          p_merchant_id: shopId,
          p_sku: sku,
        });
        if (err) throw err;
        added = ((data as any)?.added_from ?? []) as string[];
        // The server decides the new status; do not guess it locally.
        const list = await loadInsights();
        if (list) setModels(list);
      });
      return added;
    },
    [shopId, withPending, loadInsights],
  );

  /** Take a model off the store's "removed" list (same path as the catalogue's Restore). */
  const restoreModel = useCallback(
    async (sku: string) => {
      if (!shopId) throw new Error('No shop selected');
      await withPending(sku, async () => {
        const { data, error: err } = await supabase.functions.invoke('merchant-remove-products', {
          body: { shop_id: shopId, add: [], remove: [sku] },
        });
        if (err) throw err;
        if ((data as any)?.error) throw new Error((data as any).error);
        const list = await loadInsights();
        if (list) setModels(list);
      });
    },
    [shopId, withPending, loadInsights],
  );

  return { overview, models, prices, pricesLoading, error, pending, addModel, restoreModel };
};
